import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { User } from '@/api';

/* Unica fonte di verità sull'utente corrente.
 *
 * Prima non esisteva uno store: l'utente veniva rifetchato in modo
 * indipendente in quattro punti (PHeader, Pins, Boards, PinCreate) con codice
 * quasi identico, e veniva tenuto in localStorage con un TTL di 5 minuti. Non
 * essendoci modo di propagare un cambio di sessione ai componenti, il login e
 * il logout finivano con `window.location.reload()`.
 *
 * Con uno store reattivo il reload non serve, e sparisce anche la finestra in
 * cui la cache diceva "loggato" mentre la sessione lato server era già scaduta.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  /** false finché non si è saputo dal server se c'è una sessione attiva. */
  const ready = ref(false);
  const pending = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const username = computed(() => user.value?.username ?? null);

  async function load() {
    pending.value = true;
    try {
      user.value = await User.me();

      /* Se non risulta autenticato, può darsi che lo sia al bordo: dietro
       * Authentik la SPA viene servita da nginx, quindi Django non vede mai
       * il caricamento di `/` e la sessione non è ancora stata creata.
       * `/sso/session` è il solo percorso dove l'header di Authentik è
       * attendibile (in Caddy passa da forward_auth): se c'è un'identità, lì
       * il middleware fa il login e ci restituisce il cookie di sessione.
       * Se non c'è, risponde 401 e si prosegue col login normale. */
      if (!user.value) {
        const scambio = await fetch('/sso/session', { credentials: 'include' });
        if (scambio.ok) user.value = await User.me();
      }
    } catch {
      // Istanza chiusa o rete assente: in entrambi i casi non c'è un utente.
      user.value = null;
    } finally {
      pending.value = false;
      ready.value = true;
    }
  }

  async function logIn(identifier, password) {
    // `identifier` può essere username o email: se ne occupa il backend.
    user.value = await User.logIn(identifier, password);
    return user.value;
  }

  async function signUp(payload) {
    user.value = await User.signUp(payload);
    return user.value;
  }

  async function logOut() {
    try {
      await User.logOut();
    } finally {
      user.value = null;
    }
  }

  /** true se `submitter` è l'utente corrente. */
  function owns(submitter) {
    return Boolean(username.value) && submitter?.username === username.value;
  }

  return { user, ready, pending, isAuthenticated, username, load, logIn, signUp, logOut, owns };
});
